import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { SecurityUsersApi } from './rest/security-users.api';
import {
  IAssignUsersRequest,
  IBulkUpdateUsersSecurityGroupsRequest,
  ICreateUsersRequest,
  ISecurityGroupsWithSecurityGroupUserLastUpdatedInTenant,
  ISecurityUserWithGroupsQuery,
  ISecurityUserWithGroupsResponse,
} from '../../../shared/interfaces/security-group.interface';
import { IAssignUsersResponse, IGetOrCreateUserResponse, IUsersServerResponse } from '../../../shared/interfaces/user.interface';
import { IBulkAddToStaffGroupsResponse } from '../../../shared/interfaces/security.interface';
import { FileExtension } from '../../../shared/constants/file-extension';
import { IUserListReportResponse } from '../../../shared/interfaces/async-report.interface';
import { SecurityEntityLevel } from '../../../shared/constants/security-group';
import { IUserListFilterOptionsResponse } from '../../../shared/interfaces/user-list.interface';
import {
  IScopedPermissionWriteRequest,
  IScopedPermissionWriteResult,
  IUserDetailsWriteResult,
  IUserGrantsSummary,
} from '../../../shared/interfaces/permission-write.interface';
import { IPermissionTreeEntity } from '../../../shared/interfaces/permission-tree.interface';

@Injectable({
  providedIn: 'root',
})
export class SecurityUserService {
  private readonly userAssignmentsChanged$: Subject<void> = new Subject<void>();

  public readonly userAssignmentsChanges$: Observable<void> = this.userAssignmentsChanged$.asObservable();

  public constructor(private securityUsersApi: SecurityUsersApi) {}

  public notifyUserAssignmentsChanged(): void {
    this.userAssignmentsChanged$.next();
  }

  public findUsersWithSecurityGroupsByTenantId(
    options: ISecurityUserWithGroupsQuery,
  ): Observable<ISecurityUserWithGroupsResponse> {
    return this.securityUsersApi.findUsersWithSecurityGroupsByTenantId(options);
  }

  public getWorkspaceFilterOptions(term: string, page: number, limit: number): Observable<IUserListFilterOptionsResponse> {
    return this.securityUsersApi.getWorkspaceFilterOptions(term, page, limit);
  }

  public getDigitalAssetFilterOptions(
    term: string,
    page: number,
    limit: number,
    workspaceIds: string[] = [],
    ids: string[] = [],
  ): Observable<IUserListFilterOptionsResponse> {
    return this.securityUsersApi.getDigitalAssetFilterOptions(term, page, limit, workspaceIds, ids);
  }

  public getUnassignedUsers(limit: number, page: number, searchTerm?: string): Observable<IUsersServerResponse> {
    return this.securityUsersApi.getUnassignedUsers(limit, page, searchTerm);
  }

  public assignSecurityUsers(request: IAssignUsersRequest): Observable<IAssignUsersResponse> {
    return this.securityUsersApi.assignSecurityUsers(request);
  }

  public createSecurityUsers(request: ICreateUsersRequest): Observable<IGetOrCreateUserResponse> {
    return this.securityUsersApi.createSecurityUsers(request);
  }

  public removeUser(userId: string): Observable<void> {
    return this.securityUsersApi.removeUser(userId);
  }

  public getSecurityUserTenantsMembership(userId: string): Observable<ISecurityGroupsWithSecurityGroupUserLastUpdatedInTenant[]> {
    return this.securityUsersApi.getSecurityUserTenantsMembership(userId);
  }

  public addUserToSecurityGroups(userId: string, groupIds: string[]): Observable<void> {
    return this.securityUsersApi.addUserToSecurityGroups(userId, groupIds);
  }

  public updateUserProfile(userId: string, userProfile: FormData): Observable<void> {
    return this.securityUsersApi.updateUserProfile(userProfile, userId);
  }

  public updateUserSecurityGroups(userId: string, groupIds: string[], tenantId?: string): Observable<void> {
    return this.securityUsersApi.updateUserSecurityGroups(userId, groupIds, tenantId);
  }

  public updateUserDetails(userId: string, request: FormData): Observable<IUserDetailsWriteResult> {
    return this.securityUsersApi.updateUserDetails(userId, request);
  }

  public getPermissionTree(): Observable<IPermissionTreeEntity[]> {
    return this.securityUsersApi.getPermissionTree();
  }

  public getUserPermissions(userId: string): Observable<IUserGrantsSummary> {
    return this.securityUsersApi.getUserPermissions(userId);
  }

  public replaceUserPermissions(
    userId: string,
    request: IScopedPermissionWriteRequest,
  ): Observable<IScopedPermissionWriteResult> {
    return this.securityUsersApi.replaceUserPermissions(userId, request);
  }

  public bulkUpdateUsersSecurityGroups(request: IBulkUpdateUsersSecurityGroupsRequest): Observable<void> {
    return this.securityUsersApi.bulkUpdateUsersSecurityGroups(request);
  }

  public removeUserFromSecurityGroups(userId: string, groupIds: string[]): Observable<void> {
    return this.securityUsersApi.removeUserFromSecurityGroups(userId, groupIds);
  }

  public resendInvite(userId: string): Observable<void> {
    return this.securityUsersApi.resendInvite(userId);
  }

  public bulkAddToStaffSecurityGroups(
    userId: string,
    staffSecurityName: string,
    tenantIds: string[],
  ): Observable<IBulkAddToStaffGroupsResponse> {
    return this.securityUsersApi.bulkAddToStaffSecurityGroups(userId, staffSecurityName, tenantIds);
  }

  public downloadReport(
    fileExtension: FileExtension,
    workspaceId?: string,
    digitalPropertyId?: string,
  ): Observable<IUserListReportResponse> {
    return this.securityUsersApi.downloadReport(fileExtension, workspaceId, digitalPropertyId);
  }

  public generateReport(
    fileExtension: FileExtension,
    workspaceId?: string,
    digitalPropertyId?: string,
    entityLevel?: SecurityEntityLevel,
  ): Observable<IUserListReportResponse> {
    return this.securityUsersApi.generateReport(fileExtension, workspaceId, digitalPropertyId, entityLevel);
  }
}
